'use client';

import { MoreHorizontalIcon } from '@wolfejs/icons/IconMap';
import type { PgOrderEntity } from '@wolfejs/ms-api-js/src/types/orders';
import type { OrderSearchParamsQuery } from '@wolfejs/ms-api-js/src/types/pgcorp';
import { TableFilters } from '@wolfejs/ms-ui/TableFilters/TableFilters';
import type { MenuItemType } from '@wolfejs/ui/Menu';
import { MenuItem } from '@wolfejs/ui/Menu';
import { PopoverMenu } from '@wolfejs/ui/PopoverMenu';
import { Spinner } from '@wolfejs/ui/Spinner';
import { Table, type CellContext, type ObjectWithId } from '@wolfejs/ui/Table';
import type { TableColumnDef } from '@wolfejs/ui/Table/types';
import { usePathname, useRouter } from 'next/navigation';
import { DashboardPanel } from '../DashboardPanel';
import {
  OrderHistoryLabels,
  dateOptions,
  filterOptions,
  orderHistoryTableSchema,
  popoverActionItems,
  statusFilters,
} from './constants';
import { mapOrderHistoryData } from './utils';

export type OrderHistoryProps = {
  orders?: PgOrderEntity[];
  lastPage?: number;
  isLoading?: boolean;
  searchParams: OrderSearchParamsQuery;
  onSearchParamsChange?: (params: OrderSearchParamsQuery) => void;
};

export const OrderHistory = ({
  orders,
  lastPage = 1,
  isLoading,
  searchParams,
  onSearchParamsChange,
}: OrderHistoryProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const handleActionSelect = (item: MenuItemType, info: CellContext<ObjectWithId, unknown>) => {
    if (item.id === 'view') {
      let basePathname = pathname;
      if (pathname.includes('customers')) {
        const pathnameArray = pathname.split('customers');
        basePathname = `${pathnameArray[0]}/orders`;
      }
      router.push(basePathname + '/' + info.getValue());
    }
  };

  const actionsColumn: TableColumnDef<ObjectWithId> = {
    id: 'action',
    header: undefined,
    accessorKey: 'id',
    className: 'p-0 pt-1 w-13',
    cell: info => {
      return (
        <PopoverMenu
          trigger={<MoreHorizontalIcon />}
          className="not-prose"
          items={popoverActionItems}
          onSelect={item => handleActionSelect(item, info)}
          renderItem={item => <MenuItem item={item} className="dark:bg-default-900" />}
        />
      );
    },
  };

  return (
    <DashboardPanel>
      <TableFilters
        statusFilters={statusFilters}
        dateFilterOptions={dateOptions}
        defaultDateFilterOption={dateOptions[2]}
        defaultFilterOption={filterOptions[0]}
        filterOptions={filterOptions}
        exportData={mapOrderHistoryData(orders)}
        selectedStatusValue={searchParams?.status || statusFilters[0].value}
        globalSearchValue={searchParams?.search}
        onGlobalSearchChange={value => onSearchParamsChange?.({ ...searchParams, search: value ?? undefined })}
        onStatusFilterChange={value => onSearchParamsChange?.({ ...searchParams, status: value })}
        handleGenerateReport={() => {
          console.info('generate report clicked');
        }}
      />
      {isLoading ? (
        <div className="flex justify-center">
          <Spinner className="" />
        </div>
      ) : (
        <Table
          data={mapOrderHistoryData(orders)}
          schema={orderHistoryTableSchema}
          columnHeaderLabels={OrderHistoryLabels}
          columnVisibility={{ id: false }}
          columnOrder={['orderId', 'itemsCount', 'datePlaced', 'price', 'status']}
          fullWidth
          hasPagination
          manualPagination
          page={(searchParams?.pageNumber ?? 1) - 1}
          pageSize={searchParams?.pageSize ?? 10}
          pages={lastPage ?? 1}
          onPaginationChange={pagination =>
            onSearchParamsChange?.({
              ...searchParams,
              pageNumber: pagination.pageIndex + 1,
              pageSize: pagination.pageSize,
            })
          }
          isLoading={isLoading}
          extraColumns={[actionsColumn]}
          classNames={{ thead: 'bg-default-50 dark:bg-default-800', th: 'text-default-500 text-xs' }}
        />
      )}
    </DashboardPanel>
  );
};
